import StatChart, { StatChartVariant } from "@galaxy-io/dls/charts/StatChart";
import CopyInput from "@galaxy-io/dls/inputs/CopyInput";
import { InputSize, InputVariant } from "@galaxy-io/dls/inputs/Input";
import JsonViewer, { type JsonValue } from "@galaxy-io/dls/json/JsonViewer";
import Box from "@galaxy-io/dls/layout/Box";
import Link from "@galaxy-io/dls/links/Link";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Timeline, { TimelineVariant } from "@galaxy-io/dls/timeline/Timeline";
import type { RunDetail } from "@gtm/contracts/workflows/catalog";
import { styled } from "@linaria/react";
import { CodeIcon, ListChecksIcon, WrenchIcon } from "@phosphor-icons/react";
import {
  DrawerList,
  DrawerRow,
  DrawerSection,
  DrawerStack,
} from "@/components/workspace/drawer-content";
import { Feedback, Status } from "@/components/workspace/status";
import { date, duration } from "@/lib/utils";

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${t.space[8]};
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
// Steps carry a name, an outcome and sometimes a failure, so they get a row of
// their own rather than the label/value pair the metadata lists use.
// Identifiers are long enough to wrap a label/value row into a paragraph, so
// they sit in copy fields instead — the value scrolls inside the field and the
// copy button stays put next to it.
const Identifiers = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[12]};
  padding: ${t.space[12]};
  min-width: 0;
  &:not(:last-child) {
    border-bottom: 0.5px solid ${t.color.border.primary};
  }
`;
// The value column breaks anywhere so long identifiers can wrap, which would
// otherwise strand this link's trailing icon on a line of its own.
const TERMINAL = ["completed", "partial", "failed", "cancelled"];

function Identifier({ label, value }: { label: string; value: string }) {
  return (
    <CopyInput
      label={label}
      value={value}
      size={InputSize.SMALL}
      variant={InputVariant.BASE}
      family={FontFamily.MONO}
      fillWidth
    />
  );
}

/**
 * Where the run lives in Temporal and in the ingestion ledger. It sits with the
 * audit trail rather than the overview: both answer who and where, not how the
 * run went.
 */
export function RunExecution({ run }: { run: RunDetail }) {
  return (
    <DrawerSection header="Execution" icon={WrenchIcon}>
      <Identifiers>
        <Identifier label="Temporal workflow" value={run.workflowId} />
        <Identifier label="Temporal run" value={run.runId} />
        {run.id && <Identifier label="Ledger row" value={run.id} />}
      </Identifiers>
      {run.url && (
        <DrawerRow label="Temporal UI">
          <Link href={run.url} isExternal>
            <Text as="span" size={TextSize.BODY_SM}>
              Open execution
            </Text>
          </Link>
        </DrawerRow>
      )}
      {run.pendingActivities.map((activity, index) => (
        <DrawerRow key={`${activity.name}:${index}`} label={activity.name}>
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            {activity.error
              ? `Attempt ${activity.attempt} · ${activity.error}`
              : `Attempt ${activity.attempt}`}
          </Text>
        </DrawerRow>
      ))}
    </DrawerSection>
  );
}

export function RunPayloads({ run }: { run: RunDetail }) {
  return (
    <>
      <DrawerSection header="Input" icon={CodeIcon} defaultIsOpen>
        <Box maxHeight={440} overflow="auto">
          <JsonViewer
            ariaLabel="Run input"
            data={(run.input ?? null) as JsonValue}
            canSearch
            canCopy
          />
        </Box>
      </DrawerSection>
      <DrawerSection header="Output" icon={CodeIcon} defaultIsOpen>
        {run.status === "completed" ? (
          <Box maxHeight={440} overflow="auto">
            <JsonViewer
              ariaLabel="Run output"
              data={(run.output ?? null) as JsonValue}
              canSearch
              canCopy
            />
          </Box>
        ) : (
          <Text>
            {run.status === "running"
              ? "Output will appear when the run completes."
              : "This run did not return an output."}
          </Text>
        )}
      </DrawerSection>
    </>
  );
}

/**
 * A run answers three questions in order: did it work, what did it produce, and
 * where did it get stuck. Everything about execution comes from Temporal; the
 * counts come from what the run ingested.
 */
export function RunDetails({ run }: { run: RunDetail }) {
  const ran = duration(run.startedAt, run.closedAt);
  const done = run.status !== "running";
  const settled = run.tasks.filter((task) => TERMINAL.includes(task.status)).length;
  return (
    <DrawerStack>
      {run.error && <Feedback message={run.error} error />}
      <Metrics>
        {(run.deliveries
          ? [
              ["Sent", run.deliveries.sent],
              ["Failed", run.deliveries.failed + run.deliveries.unknown],
              ["Pending", run.deliveries.pending],
              ["Cancelled", run.deliveries.cancelled],
            ]
          : [
              ["Fetched", run.fetched],
              ["Added", run.added],
              ["Removed", run.removed],
              ["Baseline", run.baseline],
            ]
        ).map(([label, value]) => (
          <StatChart
            hasBorder
            key={String(label)}
            label={String(label)}
            variant={StatChartVariant.BASE}
            value={
              <Text size={TextSize.HEADING_SM} weight={TextWeight.MEDIUM}>
                {/*
                  A run that failed before it counted anything has no number to
                  show, and Number(undefined) renders "NaN" — four of them
                  across the top of the screen, which reads as a broken page
                  rather than a run that did not get that far.
                */}
                {Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—"}
              </Text>
            }
          />
        ))}
      </Metrics>
      <DrawerList>
        <DescriptionList
          items={[
            {
              label: "Status",
              value: (
                <>
                  <Status value={run.status} />
                </>
              ),
            },
            {
              label: "Trigger",
              value: (
                <>
                  <Status value={run.trigger} />
                </>
              ),
            },
            { label: "Started", value: date(run.startedAt, true), family: FontFamily.MONO },
            { label: "Finished", value: date(run.closedAt, true), family: FontFamily.MONO },
          ]}
        />
        {ran && <DrawerRow label={done ? "Took" : "Running for"}>{ran}</DrawerRow>}
        {run.appliedAt && <DrawerRow label="Ingested">{date(run.appliedAt, true)}</DrawerRow>}
        {/*
          Why it failed. The run has carried this all along and the screen never
          showed it, so a failed run gave a red badge and nothing to act on —
          the reader had to go to the logs to learn what the application already
          knew.
        */}
        {run.error && <DrawerRow label="Reason">{run.error}</DrawerRow>}
      </DrawerList>
      {!!run.tasks.length && (
        <DrawerSection
          header="Steps"
          icon={ListChecksIcon}
          defaultIsOpen
          actions={
            <Text size={TextSize.CAPTION} variant={TextVariant.SECONDARY}>
              {settled} of {run.tasks.length}
            </Text>
          }
        >
          <Timeline
            aria-label="Workflow steps"
            items={run.tasks.map((task) => {
              const took = duration(task.startedAt, task.finishedAt);
              return {
                id: task.key,
                label: task.name,
                isActive: task.status === "running",
                variant:
                  task.status === "failed"
                    ? TimelineVariant.ERROR
                    : task.status === "completed"
                      ? TimelineVariant.SUCCESS
                      : TimelineVariant.SECONDARY,
                description:
                  task.error ||
                  (took
                    ? `${task.finishedAt ? "Took" : "Running for"} ${took}`
                    : task.status === "queued"
                      ? "Not started"
                      : undefined),
                footer: <Status value={task.status} />,
              };
            })}
          />
        </DrawerSection>
      )}
    </DrawerStack>
  );
}
